import { find, findIndex, get, last, remove } from 'lodash';
import { CONTACTS_CATEGORY } from 'constants/index';
import { getIdObjectOrNull, getWithDefault, isRedMask } from './common';
import {
  SINGER_PROFESSION_TYPE_ID,
  MEDIA_TAGS_TYPES_IDS,
  PRODUCTION_ADDED_IDENTIFIERS_IDS,
  EMPTY_INFO_MARK,
  CONTACTS_NAMES,
  TP,
  SNS_CONTACTS_NAMES,
} from '../constants/consts';

export const getCorrectTabValue = (queryTab, tabValues, defaultTab) =>
  Object.values(tabValues).includes(queryTab) ? queryTab : defaultTab;

export const getArray = values => (Array.isArray(values) ? values : []);

export const checkCompletion = (completedSections, sectionName) =>
  Boolean(completedSections.find(name => name === sectionName));

export const BOX_OFFICE_CATEGORY = 'box_office';
export const POINT_OF_CONTACT_CATEGORY = 'point_of_contact';

export const EDITING_ENTITY_TYPES = {
  ORGANIZATION: 'organization',
  COMPANY: 'company',
  ARTIST: 'artist',
  FESTIVAL: 'festival',
};

export const EDITING_MEDIA_ENTITY_TYPES = {
  ...EDITING_ENTITY_TYPES,
  PRODUCTION: 'production',
};

export const setContactForUpdateAction = (name, value, category) => ({ ...value, name, ...(category && { category }) });

export const getCategory = (name, typeId) => {
  let contactType = '';
  switch (name) {
    case CONTACTS_NAMES.PHONE:
      contactType = CONTACTS_NAMES.PHONE;
      break;
    case CONTACTS_NAMES.EMAIL:
      contactType = CONTACTS_NAMES.EMAIL;
      break;
    case CONTACTS_NAMES.SNS:
    case SNS_CONTACTS_NAMES.CUETV:
    case SNS_CONTACTS_NAMES.FACEBOOK:
    case SNS_CONTACTS_NAMES.INSTAGRAM:
    case SNS_CONTACTS_NAMES.LINKEDIN:
    case SNS_CONTACTS_NAMES.TWITTER:
    case SNS_CONTACTS_NAMES.VIMEO:
    case SNS_CONTACTS_NAMES.VK:
    case SNS_CONTACTS_NAMES.YOUTUBE:
      contactType = CONTACTS_NAMES.SNS;
      break;
    case CONTACTS_NAMES.WEBSITE:
      contactType = CONTACTS_NAMES.WEBSITE;
      break;
    case CONTACTS_NAMES.BOX_OFFICE_EMAIL:
    case CONTACTS_NAMES.BOX_OFFICE_PHONE:
    case CONTACTS_NAMES.BOX_OFFICE_WEBSITE:
      contactType = CONTACTS_NAMES.BOX_OFFICE;
      break;
    case CONTACTS_NAMES.ADDRESS:
      contactType = CONTACTS_NAMES.ADDRESS;
      break;
    case CONTACTS_NAMES.OTHER:
      contactType = CONTACTS_NAMES.OTHER;
      break;
    case CONTACTS_NAMES.WORK_EMAIL:
      contactType = CONTACTS_NAMES.WORK_EMAIL;
      break;
    case CONTACTS_NAMES.WORK_PHONE:
      contactType = CONTACTS_NAMES.WORK_PHONE;
      break;
    default:
      contactType = CONTACTS_NAMES.SNS;
      break;
  }
  return {
    contactType: {
      name: contactType,
      ...((typeId !== null || typeId !== undefined) && { id: typeId }),
    },
  };
};

export const getContactCategory = (name, categoryName) => {
  let category = null;
  switch (categoryName) {
    case CONTACTS_CATEGORY.BOX_OFFICE:
      if (
        [CONTACTS_NAMES.BOX_OFFICE_PHONE, CONTACTS_NAMES.BOX_OFFICE_EMAIL, CONTACTS_NAMES.BOX_OFFICE_WEBSITE].includes(
          name,
        )
      ) {
        category = CONTACTS_CATEGORY.BOX_OFFICE;
      }
      break;
    case CONTACTS_CATEGORY.ORGANIZATION:
      category = CONTACTS_CATEGORY.ORGANIZATION;
      break;
    default:
      break;
  }
  return category;
};

const getName = name => {
  switch (name) {
    case CONTACTS_NAMES.BOX_OFFICE_EMAIL:
      return { name: CONTACTS_NAMES.EMAIL };
    case CONTACTS_NAMES.BOX_OFFICE_PHONE:
      return { name: CONTACTS_NAMES.PHONE };
    case CONTACTS_NAMES.BOX_OFFICE_WEBSITE:
      return { name: CONTACTS_NAMES.WEBSITE };
    default:
      return { name };
  }
};

const getNewContactStructure = ({
  name,
  value,
  privacyAttributes = [],
  isPrimary,
  category,
  typeId,
  placeId,
  lat,
  lon,
  city,
  country,
  contactTypeId,
}) => ({
  name,
  ...getCategory(name, typeId ?? contactTypeId),
  value,
  isPrimary,
  placeId,
  lat,
  lon,
  country,
  city,
  ...getName(name),
  privacyAttributes: privacyAttributes.map(pa => ({
    id: pa.id,
  })),
  category: getContactCategory(name, category),
});

export const markPrimaryContact = ({ newContacts, updatedContacts, deletedContacts }) => {
  const hasNewPrimaryContact = newContacts?.find(contact => contact?.isPrimary);

  if (hasNewPrimaryContact) {
    return { newContacts, updatedContacts, deletedContacts };
  }

  const hasUpdatedPrimaryContact = updatedContacts?.find(contact => contact?.isPrimary);

  if (hasUpdatedPrimaryContact) {
    return { newContacts, updatedContacts, deletedContacts };
  }

  if (newContacts?.length > 0) {
    const [firstContact, ...rest] = newContacts;

    return {
      newContacts: [{ ...firstContact, isPrimary: true }, ...rest],
      updatedContacts,
      deletedContacts,
    };
  }

  if (updatedContacts?.length > 0) {
    const [firstContact, ...rest] = updatedContacts;

    return {
      newContacts,
      updatedContacts: [{ ...firstContact, isPrimary: true }, ...rest],
      deletedContacts,
    };
  }

  return { newContacts, updatedContacts, deletedContacts };
};

export const separateContactsForUpdate = (contactsValues, oldContacts) => {
  const newContacts = [];
  const updatedContacts = [];
  const deletedContacts = [];

  // eslint-disable-next-line no-unused-expressions
  contactsValues?.forEach(value => {
    if (value?.value) {
      if (value?.id) {
        updatedContacts.push({
          id: value?.id,
          value: value?.value,
          placeId: value?.placeId,
          lat: value?.lat,
          lon: value?.lon,
          city: value?.city,
          country: value?.country,
          category: getContactCategory(value?.name, value?.category),
          isPrimary: value?.isPrimary,
          contactType: value?.contactType,
          privacyAttributes: value?.privacyAttributes?.map(pa => ({
            id: pa?.id,
          })),
        });
      } else {
        newContacts.push(getNewContactStructure(value));
      }
    } else if (value?.id) {
      deletedContacts.push(value?.id);
    }
  });

  // eslint-disable-next-line no-unused-expressions
  oldContacts?.forEach(value => {
    const matchedContact = find(contactsValues, { id: value?.id });

    if (!matchedContact) {
      deletedContacts.push(value?.id);
    }
  });
  return { newContacts, updatedContacts, deletedContacts };
};

export const changeItemInObjectsArray = (array, item, param = 'id') => {
  const data = [...getArray(array)];
  data.splice(findIndex(array, { [param]: get(item, param) }), 1, item);
  return data;
};

export const removeTagFromMediaTags = (tags, tagId, tagTypeId) => {
  const updatedTags = [...getArray(tags)];
  remove(updatedTags, { tagId, tagType: { id: tagTypeId } });
  if (tagTypeId === MEDIA_TAGS_TYPES_IDS.PRODUCTION) {
    const taggedDate = find(tags, { tagType: { id: MEDIA_TAGS_TYPES_IDS.DATE } });
    if (taggedDate?.tagId) {
      remove(updatedTags, { tagId: taggedDate?.tagId });
    }
  }
  return updatedTags;
};

export const canArtistEditProduction = (production, profileId) => {
  const isOwner =
    production?.forEntity === PRODUCTION_ADDED_IDENTIFIERS_IDS.ARTIST && production?.forEntityId === profileId;
  if (isOwner) {
    const { COMPANY, ADMIN } = PRODUCTION_ADDED_IDENTIFIERS_IDS;
    return ![COMPANY, ADMIN].includes(production?.approvedBy?.id);
  }
  return false;
};

export const isProductionType = (types, typeId) => Boolean(find(types, { id: typeId }));

export const getAcceptFormatsTypesForFiles = (type, formats) => formats?.map(f => `${type}/${f}`);

export const joinMediaFormats = (formats, separator = ', ') => formats?.map(f => f?.toUpperCase()).join(separator);

export const getProfessionRole = profession => (profession.isNew ? { name: profession?.name } : { id: profession?.id });

const getCastValue = c =>
  c.map(el => ({
    profession: { id: el?.profession?.id },
    profile: { id: el?.profile?.id },
    workRole: { id: el?.workRole?.id },
  }));

export const isCastDirty = (initialCasts, curCast) => {
  const initialCast = getCastValue(initialCasts);
  const currentCast = getCastValue(curCast);
  return JSON.stringify(initialCast) !== JSON.stringify(currentCast);
};

export const getSeparateCastsByArtistId = (casts, artistProfileId) => {
  const artistsCast = [];
  const restCast = [];
  casts.forEach(cast => {
    if (cast?.profile?.id === artistProfileId) {
      artistsCast.push({ ...cast, type: cast?.workRole || cast?.workRoleReference ? 'role' : 'profession' });
    } else {
      restCast.push(cast);
    }
  });
  return [artistsCast, restCast];
};
export const isFocusedAddAnotherField = (index, values) =>
  index === values?.length - 1 && index > 0 && !last(values)?.value;

export const getMediaTagsWithoutEntityTag = (mediaTags, tagTypeId, entityId) => {
  const updatedTags = [...(mediaTags || [])];
  remove(updatedTags, { tagType: { id: tagTypeId }, tagId: entityId });
  return updatedTags;
};

const getTrimedValueForCheckDuplicates = (obj, path) =>
  getWithDefault(obj, path, '')
    ?.trim()
    ?.toLowerCase();

export const checkDuplicateName = (profile, confirmedData) => {
  const firstName = getTrimedValueForCheckDuplicates(profile, 'firstName', '');
  const lastName = getTrimedValueForCheckDuplicates(profile, 'lastName', '');
  const fullName = getTrimedValueForCheckDuplicates(profile, 'name', '');
  const firstValuesName = getTrimedValueForCheckDuplicates(confirmedData, 'firstName', '');
  const lastValuesName = getTrimedValueForCheckDuplicates(confirmedData, 'lastName', '');
  const fullValuesName = `${firstValuesName} ${lastValuesName}`;
  return (firstName === firstValuesName && lastName === lastValuesName) || fullName === fullValuesName;
};

export const getDateValues = (values, date) => ({
  day: values[date.day.name]?.value || null,
  month: values[date.month.name]?.value || null,
  year: values[date.year.name]?.value || null,
});

export const getCastRoleName = cast =>
  cast?.workRole?.original_name ||
  cast?.workRole?.name ||
  cast?.workRoleReference ||
  cast?.profession?.name ||
  cast?.professionReference ||
  EMPTY_INFO_MARK;

export const professionStickyHeaders = [
  {
    key: 'profession',
    value: `${TP}.FN_PROFESSION`,
    width: 245,
  },
  {
    key: 'crew',
    value: `${TP}.FN_ARTIST_NAME`,
    width: 200,
  },
  {
    key: 'select_all',
    value: '',
    width: 200,
  },
];

export const professionScrollableHeaders = [
  {
    key: 'performance_dates',
    value: `${TP}.FN_PERF_DATES`,
    colSpan: 22,
  },
];

export const rolesStickyHeaders = roleName => [
  {
    key: 'role',
    value: roleName,
    width: 245,
  },
  {
    key: 'Artist',
    value: `${TP}.FN_ARTIST_NAME`,
    width: 200,
  },
  {
    key: 'select_all',
    value: '',
    width: 200,
  },
];

export const rolesScrollableHeaders = [
  {
    key: 'performance_dates',
    value: `${TP}.FN_PERF_DATES`,
    colSpan: 22,
  },
];

export const ensemblesStickyHeaders = [
  {
    key: 'ensemble',
    value: `${TP}.FN_ENSEMBLE`,
    width: 200,
  },
  {
    key: 'organization',
    value: `${TP}.m_REGISTER_JOURNALIST_NAME`,
    width: 300,
  },
  {
    key: 'select_all',
    value: '',
    width: 200,
  },
];

export const ensembleScrollableHeaders = [
  {
    key: 'performance_dates',
    value: `${TP}.FN_PERF_DATES`,
    colSpan: 22,
  },
];
